import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { CountryAliasesResponse } from '../../lib/types.js';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Select,
  StatusPill,
  TextInput,
  Toolbar,
  useConfirm,
} from '../../components/ui/index.js';
import { ADMIN_NOTE, errorMessage } from './common.js';

/**
 * Country names (Configuration → Email & Sheets → Country names): the
 * spellings a provider's "country" column is read as, on top of the
 * built-in ISO table (server ingest/country-names.ts). Adding one re-files
 * the stored rows that carry it under the code at once, so a country a
 * provider spells two ways stops splitting on the dashboard, in the
 * destination geo check and on the read-back API (owner ask 2026-09-21).
 */
export function CountryNamesCard({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const data = useQuery({
    queryKey: ['country-aliases'],
    queryFn: () => api.get<CountryAliasesResponse>('/api/country-aliases'),
  });
  const [alias, setAlias] = useState('');
  const [target, setTarget] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['country-aliases'] });
    void qc.invalidateQueries({ queryKey: ['summary'] });
    void qc.invalidateQueries({ queryKey: ['taxonomy'] });
  };
  const add = useMutation({
    mutationFn: (body: { alias: string; target: string }) =>
      api.post<{
        ok: boolean;
        alias: { alias: string; target: string; name: string | null };
        refiled: number;
        merged: number;
        kept: number;
      }>('/api/country-aliases', body),
    onSuccess: (res) => {
      const where = `${res.alias.name ?? res.alias.target} (${res.alias.target})`;
      const moved =
        res.refiled === 0
          ? 'no stored row carried it'
          : `${res.refiled} stored row${res.refiled === 1 ? '' : 's'} re-filed under ${where}` +
            (res.merged ? `, ${res.merged} folded into rows already there` : '') +
            (res.kept ? `; ${res.kept} kept apart (a twin in another currency)` : '');
      setMsg({ tone: 'ok', text: `Saved — ${moved}.` });
      setAlias('');
      setTarget('');
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'error', text: errorMessage(e) }),
  });
  const remove = useMutation({
    mutationFn: (a: string) => api.delete(`/api/country-aliases/${encodeURIComponent(a)}`),
    onSuccess: () => {
      setMsg({
        tone: 'ok',
        text: 'Removed — rows already re-filed stay under their code; the spelling is just no longer recognised on the next pull.',
      });
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'error', text: errorMessage(e) }),
  });
  const askRemove = async (a: string) => {
    const ok = await confirm<true>({
      title: `Stop reading “${a}” as a country?`,
      body: (
        <p>
          Rows already re-filed keep their code. Only future pulls of this spelling stop being
          recognised.
        </p>
      ),
      choices: [{ label: 'Remove', value: true, variant: 'danger' }],
    });
    if (ok) remove.mutate(a);
  };

  const aliases = data.data?.aliases ?? [];
  const unknown = data.data?.unknown ?? [];
  const codes = [...(data.data?.codes ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const canSave = alias.trim() !== '' && /^[A-Z]{2}$/.test(target);

  return (
    <Card
      title="Country names"
      status={
        unknown.length ? (
          <StatusPill tone="warn">{`${unknown.length} unrecognised`}</StatusPill>
        ) : data.isSuccess ? (
          <StatusPill tone="ok">All codes</StatusPill>
        ) : null
      }
      description={`How a provider's country column is read. Every country's English name, its three-letter code and the usual spellings are built in — United States, USA, U.S. → US; Germany, Deutschland, DEU → DE; UK, Great Britain, England → GB — ${data.data?.builtin ?? '…'} countries in all, and a two-letter value is taken as the code it is. Add a spelling the table does not know; the stored rows carrying it are re-filed under the code at once (folded into rows already there on the same day and feed, never counted twice, never summed across currencies), so the country stops splitting on the dashboard, in the destination geo check and on the read-back API.`}
    >
      {data.isError ? (
        <p className="ui-field__error">Could not load the aliases: {errorMessage(data.error)}</p>
      ) : null}
      {unknown.length ? (
        <>
          <p className="table-note">
            <b>Stored values that are not a country code:</b> map each to its country below, or
            leave it if it is not a country at all.
          </p>
          <ul className="table-note" data-unknown-countries>
            {unknown.map((u) => (
              <li key={u.value}>
                <code>{u.value}</code> · {u.rows.toLocaleString('en-US')} row
                {u.rows === 1 ? '' : 's'} · last {u.lastDay}
                {isAdmin ? (
                  <>
                    {' '}
                    <Button variant="ghost" size="sm" onClick={() => setAlias(u.value)}>
                      Map
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : data.isSuccess ? (
        <p className="table-note">Every stored country is a code the table knows.</p>
      ) : null}
      {aliases.length ? (
        <div className="table-scroll">
          <table className="stat-table">
            <thead>
              <tr>
                <th className="l">Spelling</th>
                <th className="l">Read as</th>
                {isAdmin ? <th className="l" /> : null}
              </tr>
            </thead>
            <tbody>
              {aliases.map((a) => (
                <tr key={a.alias}>
                  <td className="l">{a.alias}</td>
                  <td className="l">
                    {a.name ?? a.target} ({a.target})
                  </td>
                  {isAdmin ? (
                    <td className="l">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => void askRemove(a.alias)}
                      >
                        Delete
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {isAdmin ? (
        <>
          <div className="cfg-grid">
            <Field
              label="Spelling"
              hint="as the provider writes it; case, dots and accents do not matter"
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  value={alias}
                  maxLength={80}
                  onChange={(e) => setAlias(e.target.value)}
                />
              )}
            </Field>
            <Field label="Country">
              {(aria) => (
                <Select {...aria} value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">— choose —</option>
                  {codes.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Toolbar className="cfg-actions">
            <Button
              size="sm"
              loading={add.isPending}
              disabled={!canSave}
              onClick={() => add.mutate({ alias: alias.trim(), target })}
            >
              Add spelling
            </Button>
          </Toolbar>
        </>
      ) : (
        <EmptyState>{ADMIN_NOTE}</EmptyState>
      )}
      {msg ? (
        <p className={msg.tone === 'ok' ? 'ui-field__hint' : 'ui-field__error'}>{msg.text}</p>
      ) : null}
      {dialog}
    </Card>
  );
}
